import type { HttpResponse } from '@brickflow/http'

import { storageService } from '@/core/services'

import { landingUrlMap } from '../constants/route'

export const errorListStatus = [200, 400, 401, 403, 429, 451, 500, 530] as const

export type HttpErrorMapperKeys = (typeof errorListStatus)[number]
type ErrorFunction = (
  response: HttpResponse<Record<string, unknown> & { error_message: string; params: string; status: 'error' }>,
) => void
type HttpErrorMapper = Record<(typeof errorListStatus)[number], ErrorFunction>

export function isErrorStatus<T>(value: number | T): value is (typeof errorListStatus)[number] {
  return errorListStatus.some((status) => status === value)
}

function isNotEnoughBalance(message: string | undefined): boolean {
  const { tApi } = useLocale()
  return Boolean(message) && (message === 'need_sum' || message === tApi('need_sum'))
}

const serverError: ErrorFunction = (response) => {
  const { data, status } = response

  showError({ data, statusCode: status })
  requestAnimationFrame(() => {
    showError({ data, statusCode: status })
  })
  setTimeout(() => {
    showError({ data, statusCode: status })
  }, 1000)
}

const notify = useNotify()
export const httpErrorMapper: HttpErrorMapper = {
  200: (response) => {
    const { data } = response

    if (isNotEnoughBalance(data?.error_message)) {
      const dialog = useDialog()
      dialog.open('fastDeposit')
      return
    }

    // if (data?.error_message === 'need_auth') {
    //   const { $di } = useNuxtApp()
    //   const route = useRoute()
    //   await $di.root.init()
    //   const userStore = useBrickUserStore()
    //   const config = useRuntimeConfig()
    //   if (!userStore.isAuth && getName(config) === 'spinarium') {
    //     if (route.query[Query.Auth] !== 'register' && !userStore.isAuth) {
    //       const dialog = useDialog()
    //       dialog.open('signIn')
    //     }
    //   }
    //   return
    // }
    if (data?.error_message === 'login_email_already_used') {
      const confirm = data?.auto_link_social_confirm as { email: string; token: string }
      storageService.set('social-confirm-token', confirm?.token)
      const dialog = useDialog()
      dialog.open('signIn', { email: confirm?.email })
      return
    }
  },
  400: async (response) => {
    const { data, status } = response
    if (data?.error_message === '2fa.required_auth') {
      showError({
        data: { ...data, isTwoFactor: true },
        statusCode: status,
      })
      return
    }
    if (data?.error_message === 'phone_confirm_required') {
      const { t, tApi } = useLocale()
      notify.create({
        description: tApi(data.error_message),
        title: t('Error!'),
        type: 'error',
      })
      return
    }
    if (data?.error_message === 'payout_promocode_minimum_refill') {
      const { t, tApi } = useLocale()
      notify.create({
        description: sprintfTranslate(tApi(data.error_message), [String(data?.min_amount)]),
        title: t('Error!'),
        type: 'error',
      })
      return
    }
    if (data?.error_message === 'bonus.promocode_conditions_error') {
      const { t, tApi } = useLocale()

      notify.create({
        description: sprintfTranslate(tApi(data.error_message), [String(5)]),
        title: t('Error!'),
        type: 'error',
      })
      return
    }
    if (data?.error_message === 'payout.transfer_delay_available') {
      const { t, tApi } = useLocale()
      notify.create({
        description: sprintfTranslate(tApi(data.error_message), [data?.params]),
        title: t('Error!'),
        type: 'error',
      })
      return
    }
    if (data?.error_message === 'bonus_balance_bet_part_limit') {
      const { t, tApi } = useLocale()
      notify.create({
        description: sprintfTranslate(tApi(data.error_message), [String(data?.max)]),
        title: t('Error!'),
        type: 'error',
      })
      return
    }
    if (data?.error_message === 'payout.locked_balance') {
      const { t, tApi } = useLocale()

      const userStore = useBrickUserStore()
      notify.create({
        currencyPrecision: true,
        description: sprintfTranslate(tApi(data.error_message), [String(userStore?.balanceLocked)]),
        title: t('Play Required'),
        type: 'error',
      })
      return
    }
    if (data?.error_message === 'bonus_balance_active_max_bet') {
      const { t, tApi } = useLocale()

      notify.create({
        description: sprintfTranslate(tApi(data.error_message), [String(data?.max)]),
        title: t('Error!'),
        type: 'error',
      })
      return
    }
    if (data?.error_message === 'kyc_verification_required') {
      const { t } = useLocale()

      const userStore = useBrickUserStore()
      const link = data?.external_kyc_link ?? userStore.user?.external_kyc_link
      if (isEmpty(link)) {
        notify.create({
          description: t('Please verify your account'),
          title: t('Error!'),
          type: 'error',
        })
        return
      }

      const dialog = useDialog()
      dialog.open('sumSub', {
        externalLink: data?.external_kyc_link,
      })
      return
    }
    if (data?.error_message.startsWith('promocode.')) {
      const { t, tApi } = useLocale()

      const { error_message, status: _status, ...params } = data
      notify.create({
        description: sprintfTranslate(tApi(data.error_message), params as Record<string, string>),
        title: t('Error!'),
        type: 'error',
      })
      return
    }
    if (data?.error_message) {
      const { localePath, t, tApi } = useLocale()

      const dialog = useDialog()

      if (data.error_message === 'bonus.geo_restriced') {
        await dialog.open('priorityNotify', {
          info: {
            id: null,
            notice_type: 'error',
            readed: false,
            short_text: t('This bonus is not available in your region'),
            time_publish_up: '',
            title: t('Bonus unavailable'),
          },
        })
        await navigateTo(localePath('/'))
        return
      } else if (isNotEnoughBalance(data.error_message)) {
        dialog.open('fastDeposit')
      } else if (data.error_message === 'main.self_exclusion.ban') {
        notify.create({
          description: sprintfTranslate(
            t('You have used self-exclusion on your account, the date of removal of the restriction is %s'),
            [formatDate(createDateFromUnix(Number(data?.date)), 'YYYY.MM.DD')],
          ),
          title: t('Error!'),
          type: 'error',
        })
      } else if (data.error_message === 'main.cooling_off.ban') {
        notify.create({
          description: sprintfTranslate(
            t('You have used cooling-off on your account, the date of removal of the restriction is %s'),
            [formatDate(createDateFromUnix(Number(data?.date)), 'YYYY.MM.DD')],
          ),
          title: t('Error!'),
          type: 'error',
        })
      } else {
        const gameStore = useBrickGameStore()
        const hasMax = data?.max !== undefined && data?.max !== null
        const withMax = hasMax && data.error_message.includes('%s')
        notify.create({
          currencyCode: withMax ? (gameStore.gameCurrency ?? undefined) : undefined,
          description: withMax
            ? sprintfTranslate(tApi(data.error_message), [String(data.max)])
            : tApi(data.error_message),
          title: t('Error!'),
          type: 'error',
        })
      }
    }
  },
  401: async () => {
    // const { $di } = useNuxtApp()
    // const route = useRoute()
    // await $di.root.init()
    // const userStore = useBrickUserStore()
    // if (!userStore.isAuth) {
    //   const dialog = useDialog()
    //   if (route.query[Query.Auth] !== 'register' && !userStore.isAuth) {
    //     dialog.open('signIn')
    //   }
    // }
  },
  403: (response) => {
    const { data, status } = response
    if (data?.error_message === 'banned.you_banned') {
      showError({ data: { ...data, isBan: true }, statusCode: status })
    }
  },
  429: () => {
    const { t, tApi } = useLocale()

    notify.create({
      description: tApi('try_later'),
      title: t('Error!'),
      type: 'error',
    })
  },
  451: (response) => {
    const { data, status } = response

    const route = useRoute()
    const rootStore = useBrickRootStore()
    rootStore.setUserGeo(String(data?.country), String(data?.ip), true)
    const { getRealPath } = useLocale()
    if (landingUrlMap[getRealPath(route.path)]) {
      return
    }

    showError({
      data: { data, isRestrictionRegion: true },
      statusCode: status,
    })
  },
  500: serverError,
  530: serverError,
}
